/**
 * Upload handler for ceSDK Upload tab.
 * Handles file uploads to S3 and collections, matching library page upload flow.
 */

import {
  uploadFile,
  uploadFileToCollection,
} from "../api/media-library/upload-file";
import { FileToUpload } from "../api/media-library/types";
import { getOrCreateEditorUploadsCollection } from "./cesdk-editor-collection";
import { collectionFileToAssetDefinition } from "./cesdk-asset-converter";

/**
 * AssetDefinition type from ceSDK
 * Based on ceSDK v1.53.0 AssetDefinition interface
 */
export interface AssetDefinition {
  id: string;
  meta?: {
    uri: string;
    thumbUri?: string;
    width?: number;
    height?: number;
    blockType?: string; // Required for ceSDK to properly handle assets (e.g., "//ly.img.ubq/graphic", "//ly.img.ubq/video")
    kind?: string; // Asset kind: "image", "video", "audio", etc.
    mimeType?: string; // MIME type of the asset
    fillType?: string; // Fill type for images (e.g., "//ly.img.ubq/fill/image")
    shapeType?: string; // Shape type for images (e.g., "//ly.img.ubq/shape/rect")
  };
  label?: string;
  tags?: string[];
  // Optional properties that may be added when returning from findAssets
  context?: {
    sourceId: string;
  };
  locale?: string;
}

/**
 * Handle file upload for ceSDK Upload tab
 * @param file - Native File object from ceSDK
 * @param onProgress - Progress callback (0-100)
 * @param context - Upload context with sourceId for per-section validation
 * @returns AssetDefinition for ceSDK to use
 */
export async function handleCESDKUpload(
  file: File,
  onProgress: (progress: number) => void,
  context?: { sourceId?: string; group?: string }
): Promise<AssetDefinition> {
  try {
    const mimeType = file.type || "application/octet-stream";
    const fileName = file.name.toLowerCase();
    const sourceId = context?.sourceId;

    // Per-section MIME type validation
    if (sourceId === "sked-uploads-video" && !mimeType.startsWith("video/")) {
      throw new Error(
        "Only video files can be uploaded to the Videos section. Supported: MP4, MOV, WebM"
      );
    }
    if (sourceId === "sked-uploads-image" && !mimeType.startsWith("image/")) {
      throw new Error(
        "Only image files can be uploaded to the Images section. Supported: PNG, JPEG, JPG, GIF, WEBP, BMP, TIFF"
      );
    }
    if (sourceId === "sked-uploads-audio" && !mimeType.startsWith("audio/")) {
      throw new Error(
        "Only audio files can be uploaded to the Audio section. Supported: MP3, WAV, AAC, M4A"
      );
    }

    // Block SVG uploads (security - XSS vulnerability)
    if (
      mimeType === "image/svg+xml" ||
      mimeType?.includes("svg") ||
      fileName.endsWith(".svg")
    ) {
      throw new Error("SVG files are not allowed for security reasons");
    }

    // Block HEIC uploads (not supported by library)
    if (
      mimeType === "image/heic" ||
      mimeType === "image/heif" ||
      fileName.endsWith(".heic") ||
      fileName.endsWith(".heif")
    ) {
      throw new Error("HEIC/HEIF files are not supported");
    }

    const isVideo = mimeType.startsWith("video/");
    const isImage = mimeType.startsWith("image/");
    const isAudio = mimeType.startsWith("audio/");

    // Validate audio file extensions
    const validAudioExtensions = [".mp3", ".wav", ".aac", ".m4a"];
    if (
      isAudio &&
      !validAudioExtensions.some((ext) => fileName.endsWith(ext))
    ) {
      throw new Error(
        "Unsupported audio format. Supported: MP3, WAV, AAC, M4A"
      );
    }

    // Step 1: Get or create "Editor Uploads" collection
    const collectionId = await getOrCreateEditorUploadsCollection();

    // Step 2: Convert File to FileToUpload format
    const fileToUpload: FileToUpload = {
      name: file.name,
      type: isVideo
        ? "video"
        : isAudio
          ? "audio"
          : isImage
            ? "image"
            : "document",
      contentType: "blob",
      blob: file,
    };

    // Step 3: Upload file using library upload flow
    // This handles S3 upload and video transcoding
    // For videos: 0-50% = S3 upload, 50-95% = transcoding, 95-100% = finalizing
    // For images/audio: 0-95% = S3 upload, 95-100% = finalizing
    const uploadResult = await uploadFile(fileToUpload, {
      onProgress: ({ value, status }) => {
        if (isVideo) {
          // For videos, split progress between upload (0-50%) and transcoding (50-95%)
          if (status === "uploading") {
            // S3 upload: map 0-100 to 0-50
            onProgress(Math.min(value * 0.5, 50));
          } else if (status === "transcoding") {
            // Transcoding: map 0-100 to 50-95
            onProgress(50 + Math.min(value * 0.45, 45));
          }
        } else {
          // For images/audio, map progress to 0-95% (leave room for finalization)
          onProgress(Math.min(value * 0.95, 95));
        }
      },
    });

    // Step 4: Add file to collection
    const collectionFile = await uploadFileToCollection({
      collectionId,
      file: {
        ...uploadResult,
        name: file.name,
      },
    });

    // Step 5: Convert CollectionFile to AssetDefinition
    const assetDefinition = collectionFileToAssetDefinition(collectionFile);

    onProgress(100);
    return assetDefinition;
  } catch (error) {
    console.error("ceSDK upload failed:", error);
    throw new Error(
      `Failed to upload file: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
